import { useEffect, useState, type FormEvent } from 'react';
import { useHref, useRender, useT } from '../context';
import type { UiKey } from '../i18n';
import { loadRecaptcha, recaptchaToken } from '../recaptcha';
import { RichHtml, TitleHighlight } from './common';

export interface ContactFormProps {
  title: string;
  highlight: string;
  introHtml: string;
  submitLabel: string;
  successHref?: string;
}

type Field = 'name' | 'email' | 'phone' | 'licensePlate' | 'message';
type Status = { kind: 'idle' } | { kind: 'sending' } | { kind: 'success' } | { kind: 'error'; key: UiKey };

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE = /^[0-9 +()/-]{5,40}$/;

/**
 * Replaces the legacy form that posted to "sendmail.php". Submissions go to the API, which validates, applies
 * spam checks and rate limits, stores the message and notifies admins. The visitor sees the real API outcome.
 */
export function ContactForm({ title, highlight, introHtml, submitLabel, successHref }: ContactFormProps) {
  const t = useT();
  const { apiBase, lang, mode, page } = useRender();
  const resolve = useHref();
  const [values, setValues] = useState<Record<Field, string>>({ name: '', email: '', phone: '', licensePlate: '', message: '' });
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Field, UiKey>>>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [token, setToken] = useState<string | null>(null);
  const [siteKey, setSiteKey] = useState<string | null>(null);

  const loadToken = async () => {
    try {
      const res = await fetch(`${apiBase}/api/public/contact/token`, { credentials: 'same-origin' });
      if (res.ok) {
        const body = await res.json();
        setToken(body.token);
        setSiteKey(body.recaptchaSiteKey ?? null);
      }
    } catch {
      /* shown as an error on submit */
    }
  };

  useEffect(() => {
    if (mode === 'editor') return;
    void loadToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const validate = () => {
    const e: Partial<Record<Field, UiKey>> = {};
    if (values.name.trim().length < 2) e.name = 'fieldRequired';
    if (!EMAIL.test(values.email.trim())) e.email = values.email.trim() ? 'fieldEmail' : 'fieldRequired';
    if (values.phone.trim() && !PHONE.test(values.phone.trim())) e.phone = 'fieldPhone';
    if (values.message.trim().length < 5) e.message = 'fieldMessage';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (mode === 'editor' || status.kind === 'sending') return;
    if (!validate()) {
      setStatus({ kind: 'error', key: 'formInvalid' });
      return;
    }
    setStatus({ kind: 'sending' });
    try {
      let captcha: string | null;
      try {
        captcha = await recaptchaToken(siteKey, 'contact_submit');
      } catch {
        return setStatus({ kind: 'error', key: 'captchaFailed' });
      }
      const res = await fetch(`${apiBase}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ...values,
          phone: values.phone || null,
          licensePlate: values.licensePlate || null,
          language: lang,
          sourcePath: page?.path ?? (typeof window !== 'undefined' ? window.location.pathname : null),
          website,
          token: token ?? '',
          recaptchaToken: captcha,
        }),
      });
      if (res.status === 202 || res.ok) {
        setStatus({ kind: 'success' });
        const target = resolve(successHref);
        if (target) window.location.assign(target);
        return;
      }
      if (res.status === 429) return setStatus({ kind: 'error', key: 'formTooMany' });
      const body = await res.json().catch(() => null);
      if (body?.title === 'captcha_failed') return setStatus({ kind: 'error', key: 'captchaFailed' });
      if (body?.title === 'captcha_unavailable') return setStatus({ kind: 'error', key: 'formUnavailable' });
      if (body?.title === 'form_expired') {
        await loadToken();
        return setStatus({ kind: 'error', key: 'formExpired' });
      }
      if (res.status === 400 && body?.errors) {
        const map: Record<string, Field> = { Name: 'name', Email: 'email', Phone: 'phone', LicensePlate: 'licensePlate', Message: 'message' };
        const e: Partial<Record<Field, UiKey>> = {};
        for (const k of Object.keys(body.errors)) if (map[k]) e[map[k]] = map[k] === 'email' ? 'fieldEmail' : map[k] === 'phone' ? 'fieldPhone' : 'fieldRequired';
        setErrors(e);
        return setStatus({ kind: 'error', key: 'formInvalid' });
      }
      setStatus({ kind: 'error', key: 'formError' });
    } catch {
      setStatus({ kind: 'error', key: 'formError' });
    }
  };

  const input = (field: Field, placeholder: UiKey, type = 'text', extraClass = 'style-two', autoComplete?: string) => (
    <div className={`form-group ${extraClass}`}>
      <label className="sr-only" htmlFor={`cf-${field}`}>
        {t(placeholder)}
      </label>
      <input
        id={`cf-${field}`}
        type={type}
        name={field}
        className={`form-control${errors[field] ? ' kfz-invalid' : ''}`}
        placeholder={t(placeholder)}
        value={values[field]}
        autoComplete={autoComplete}
        aria-invalid={errors[field] ? true : undefined}
        aria-describedby={errors[field] ? `cf-${field}-err` : undefined}
        onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
        disabled={mode === 'editor'}
      />
      {errors[field] ? (
        <span id={`cf-${field}-err`} className="kfz-field-error">
          {t(errors[field]!)}
        </span>
      ) : null}
    </div>
  );

  return (
    <section className="contact_us">
      <div className="container">
        <div className="sec-title text-center">
          <h2>
            <TitleHighlight title={title} highlight={highlight} />
          </h2>
          <RichHtml html={introHtml} className="kfz-rich" />
        </div>
        <div className="default-form-area">
          <form
            id="contact-form"
            className="default-form"
            noValidate
            onSubmit={onSubmit}
            onFocus={() => {
              // Google's script is only loaded once somebody starts filling in the form.
              if (siteKey && mode !== 'editor') void loadRecaptcha(siteKey).catch(() => undefined);
            }}
          >
            <div className="row clearfix">
              <div className="col-md-6 col-sm-6 col-xs-12">{input('name', 'formName', 'text', 'style-two', 'name')}</div>
              <div className="col-md-6 col-sm-6 col-xs-12">{input('email', 'formEmail', 'email', 'style-two', 'email')}</div>
              <div className="col-md-6 col-sm-6 col-xs-12">{input('phone', 'formPhone', 'tel', 'style-two', 'tel')}</div>
              <div className="col-md-6 col-sm-6 col-xs-12">{input('licensePlate', 'formPlate', 'text', '')}</div>
              <div className="col-md-12 col-sm-12 col-xs-12">
                <div className="form-group style-two">
                  <label className="sr-only" htmlFor="cf-message">
                    {t('formMessage')}
                  </label>
                  <textarea
                    id="cf-message"
                    name="message"
                    className={`form-control textarea${errors.message ? ' kfz-invalid' : ''}`}
                    placeholder={t('formMessage')}
                    value={values.message}
                    aria-invalid={errors.message ? true : undefined}
                    aria-describedby={errors.message ? 'cf-message-err' : undefined}
                    onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
                    disabled={mode === 'editor'}
                  />
                  {errors.message ? (
                    <span id="cf-message-err" className="kfz-field-error">
                      {t(errors.message)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            {/* Honeypot: invisible for people, filled by bots. */}
            <div className="kfz-hp" aria-hidden="true">
              <label htmlFor="cf-website">Website</label>
              <input id="cf-website" type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>
            <div className="contact-section-btn text-center">
              <div className="form-group style-two">
                <button className="thm-btn thm-color" type="submit" disabled={status.kind === 'sending' || mode === 'editor'}>
                  {status.kind === 'sending' ? t('formSending') : submitLabel}
                </button>
              </div>
              <div role="status" aria-live="polite" className="kfz-form-status">
                {status.kind === 'success' ? <p className="kfz-form-success">{t('formSuccess')}</p> : null}
                {status.kind === 'error' ? <p className="kfz-form-error">{t(status.key)}</p> : null}
              </div>
              {siteKey ? <p className="kfz-recaptcha-note">{t('recaptchaNotice')}</p> : null}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
